import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

async function authedFetch(path: string, token: string | null, init?: RequestInit): Promise<unknown> {
  const headers = new Headers(init?.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init?.body) headers.set('Content-Type', 'application/json');
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status}: ${body}`);
  }
  return res.json();
}

export const ParserHintSchema = z.object({
  format: z.string().optional(),
  pattern: z.string().optional(),
  params: z.record(z.unknown()).optional(),
}).passthrough();

export const AttributeSchema = z.object({
  name: z.string().regex(/^[a-z][a-z0-9_]*$/),
  type: z.enum(['text', 'int', 'enum', 'date', 'bool']),
  required: z.boolean().default(false),
  voice_role: z.enum(['name', 'discriminator', 'info', 'none']),
  voice_speak_template: z.string().nullable().optional(),
  parser_hint: ParserHintSchema.optional(),
  params: z.record(z.unknown()).optional(),
});
export type Attribute = z.infer<typeof AttributeSchema>;

export const ItemSchemaSchema = z.object({
  id: z.number(),
  tenant_id: z.number(),
  warehouse_id: z.number(),
  version: z.number(),
  is_active: z.boolean(),
  schema: z.object({ attributes: z.array(AttributeSchema) }),
  created_at: z.string(),
  created_by_user_id: z.number().nullable(),
});
export type ItemSchema = z.infer<typeof ItemSchemaSchema>;

export function useListItemSchemas(warehouseId: number | null, token: string | null) {
  return useQuery({
    queryKey: ['itemSchemas', warehouseId],
    queryFn: () =>
      authedFetch(`/admin/platform/item-schemas?warehouse_id=${warehouseId}&limit=200`, token) as Promise<{
        items: ItemSchema[];
        next_cursor: string | null;
      }>,
    enabled: warehouseId !== null && token !== null,
  });
}

export function useItemSchema(id: number | null, token: string | null) {
  return useQuery({
    queryKey: ['itemSchema', id],
    queryFn: () => authedFetch(`/admin/platform/item-schemas/${id}`, token) as Promise<ItemSchema>,
    enabled: id !== null && token !== null,
  });
}

export function useActiveItemSchema(warehouseId: number | null, token: string | null) {
  return useQuery({
    queryKey: ['itemSchema', 'active', warehouseId],
    queryFn: () =>
      authedFetch(`/admin/platform/item-schemas/active?warehouse_id=${warehouseId}`, token).catch(() => null) as Promise<ItemSchema | null>,
    enabled: warehouseId !== null && token !== null,
  });
}

export function useCreateItemSchema(token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { warehouse_id: number; schema: { attributes: Attribute[] } }) =>
      authedFetch('/admin/platform/item-schemas', token, {
        method: 'POST',
        body: JSON.stringify(input),
      }) as Promise<ItemSchema>,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['itemSchemas', data.warehouse_id] });
      qc.invalidateQueries({ queryKey: ['itemSchema', 'active', data.warehouse_id] });
    },
  });
}

export function useActivateItemSchema(token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      authedFetch(`/admin/platform/item-schemas/${id}/activate`, token, { method: 'POST' }) as Promise<{ id: number; is_active: boolean }>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['itemSchemas'] });
      qc.invalidateQueries({ queryKey: ['itemSchema'] });
    },
  });
}
